import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  ChevronLeft,
  ChevronRight,
  Edit,
  Loader2,
  Plus,
  Search,
  Trash2,
  UserPlus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { useGetUsersPaged, useCreateUser, useUpdateUser, useDeleteUser } from '../hooks/User';
import { BaseUser, CreateUser, UpdateUser } from '../dtos/Users/index';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';

import { USER_ROLE_NAMES, getUserRoleFromString } from '../dtos/Enums';

const AVAILABLE_ROLES = USER_ROLE_NAMES;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

// Map role to lowercase for display
const getRoleDisplay = (role: string): string => {
  return role.toLowerCase();
};

const userSchema = z.object({
  firstName: z.string().min(1, 'Prenumele este obligatoriu'),
  lastName: z.string().min(1, 'Numele este obligatoriu'),
  email: z.string().email('Email invalid').min(1, 'Email-ul este obligatoriu'),
  phone: z.string().optional(),
  password: z.string().optional(),
  roles: z.array(z.string()).min(1, 'Selectați cel puțin un rol'),
});

const createUserSchema = userSchema.extend({
  password: z.string().min(6, 'Parola trebuie să aibă minim 6 caractere'),
});

type UserFormValues = z.infer<typeof userSchema>;

export default function Users() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<BaseUser | null>(null);

  // Get URL params
  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('size') || '10', 10);
  const searchTerm = searchParams.get('search') || '';

  // Sync search input with URL param on mount
  useEffect(() => {
    setSearchInput(searchTerm);
  }, [searchTerm]);

  const { data: usersResponse, isLoading } = useGetUsersPaged(page, pageSize, searchTerm || undefined);
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();

  const pagedData = usersResponse?.isSuccess ? usersResponse.data : null;
  const users = pagedData?.items || [];
  const totalCount = pagedData?.totalCount || 0;
  const totalPages = pagedData?.totalPages || 1;

  // Update URL params
  const updateParams = (updates: { page?: number; size?: number; search?: string }) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (updates.page !== undefined) {
      if (updates.page === 1) {
        newParams.delete('page');
      } else {
        newParams.set('page', updates.page.toString());
      }
    }
    
    if (updates.size !== undefined) {
      if (updates.size === 10) {
        newParams.delete('size');
      } else {
        newParams.set('size', updates.size.toString());
      }
    }
    
    if (updates.search !== undefined) {
      if (updates.search === '') {
        newParams.delete('search');
      } else {
        newParams.set('search', updates.search);
      }
      // Reset to page 1 when searching
      newParams.delete('page');
    }
    
    setSearchParams(newParams);
  };

  const handleSearch = () => {
    updateParams({ search: searchInput });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    updateParams({ search: '' });
  };

  const createForm = useForm<UserFormValues>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      roles: [],
    },
  });

  const editForm = useForm<UserFormValues>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      roles: [],
    },
  });

  const handleCreateUser = async (values: UserFormValues) => {
    const createData: CreateUser = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone || undefined,
      password: values.password,
      roles: values.roles,
    };

    const response = await createUserMutation.mutateAsync(createData);
    if (response.isSuccess) {
      setIsCreateDialogOpen(false);
      createForm.reset();
    }
  };

  const handleEditUser = async (values: UserFormValues) => {
    if (!selectedUser) return;

    const updateData: Omit<UpdateUser, 'id'> = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone || undefined,
      password: values.password || undefined,
      roles: values.roles,
    };

    const response = await updateUserMutation.mutateAsync({
      id: selectedUser.id,
      data: updateData,
    });

    if (response.isSuccess) {
      setIsEditDialogOpen(false);
      setSelectedUser(null);
      editForm.reset();
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    const response = await deleteUserMutation.mutateAsync(selectedUser.id);
    if (response.isSuccess) {
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const openEditDialog = (user: BaseUser) => {
    setSelectedUser(user);
    editForm.reset({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '',
      roles: user.roles || [],
    });
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (user: BaseUser) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const RoleCheckboxes = ({
    value,
    onChange,
  }: {
    value: string[];
    onChange: (roles: string[]) => void;
  }) => (
    <div className="grid grid-cols-2 gap-2">
      {AVAILABLE_ROLES.map((role) => (
        <div key={role} className="flex items-center space-x-2">
          <Checkbox
            id={`role-${role}`}
            checked={value.includes(role)}
            onCheckedChange={(checked) => {
              if (checked) {
                onChange([...value, role]);
              } else {
                onChange(value.filter((r) => r !== role));
              }
            }}
          />
          <Label htmlFor={`role-${role}`} className="text-sm font-normal cursor-pointer">
            {getRoleDisplay(role)}
          </Label>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Utilizatori</h1>
          <p className="text-muted-foreground mt-2">
            Gestionați utilizatorii și permisiunile acestora.
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Adaugă Utilizator
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5" />
            Lista Utilizatori
          </CardTitle>
          <CardDescription>
            {totalCount} utilizatori în total
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Caută utilizatori..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="pl-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={clearSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <Button onClick={handleSearch} variant="secondary">
              <Search className="h-4 w-4 mr-2" />
              Caută
            </Button>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <UsersIcon className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? 'Nu s-au găsit utilizatori.' : 'Nu există utilizatori.'}
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nume</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Telefon</TableHead>
                      <TableHead>Roluri</TableHead>
                      <TableHead className="w-[100px]">Acțiuni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          {user.firstName} {user.lastName}
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.phone || '-'}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {user.roles?.map((role) => (
                              <Badge key={role} variant="secondary" className="text-xs">
                                {getRoleDisplay(role)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => openEditDialog(user)}
                              title="Editează"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => openDeleteDialog(user)}
                              title="Șterge"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Rânduri pe pagină:</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => updateParams({ size: parseInt(value, 10), page: 1 })}
                  >
                    <SelectTrigger className="w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <SelectItem key={size} value={size.toString()}>
                          {size}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">
                    Pagina {page} din {totalPages}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page - 1 })}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateParams({ page: page + 1 })}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Adaugă Utilizator Nou
            </DialogTitle>
            <DialogDescription>
              Completați informațiile pentru noul utilizator.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createForm.handleSubmit(handleCreateUser)}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="create-firstName">Prenume</Label>
                  <Input
                    id="create-firstName"
                    {...createForm.register('firstName')}
                    className={createForm.formState.errors.firstName ? 'border-destructive' : ''}
                  />
                  {createForm.formState.errors.firstName && (
                    <p className="text-sm text-destructive">
                      {createForm.formState.errors.firstName.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-lastName">Nume</Label>
                  <Input
                    id="create-lastName"
                    {...createForm.register('lastName')}
                    className={createForm.formState.errors.lastName ? 'border-destructive' : ''}
                  />
                  {createForm.formState.errors.lastName && (
                    <p className="text-sm text-destructive">
                      {createForm.formState.errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-email">Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  {...createForm.register('email')}
                  className={createForm.formState.errors.email ? 'border-destructive' : ''}
                />
                {createForm.formState.errors.email && (
                  <p className="text-sm text-destructive">
                    {createForm.formState.errors.email.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-phone">Telefon</Label>
                <Input id="create-phone" {...createForm.register('phone')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-password">Parolă</Label>
                <Input
                  id="create-password"
                  type="password"
                  {...createForm.register('password')}
                  className={createForm.formState.errors.password ? 'border-destructive' : ''}
                />
                {createForm.formState.errors.password && (
                  <p className="text-sm text-destructive">
                    {createForm.formState.errors.password.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Roluri</Label>
                <RoleCheckboxes
                  value={createForm.watch('roles') || []}
                  onChange={(roles) => createForm.setValue('roles', roles, { shouldValidate: true })}
                />
                {createForm.formState.errors.roles && (
                  <p className="text-sm text-destructive">
                    {createForm.formState.errors.roles.message}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  createForm.reset();
                }}
              >
                Anulează
              </Button>
              <Button type="submit" disabled={createUserMutation.isPending}>
                {createUserMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Se creează...
                  </>
                ) : (
                  'Creează Utilizator'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Editează Utilizator
            </DialogTitle>
            <DialogDescription>
              Modificați informațiile utilizatorului.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={editForm.handleSubmit(handleEditUser)}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-firstName">Prenume</Label>
                  <Input
                    id="edit-firstName"
                    {...editForm.register('firstName')}
                    className={editForm.formState.errors.firstName ? 'border-destructive' : ''}
                  />
                  {editForm.formState.errors.firstName && (
                    <p className="text-sm text-destructive">
                      {editForm.formState.errors.firstName.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-lastName">Nume</Label>
                  <Input
                    id="edit-lastName"
                    {...editForm.register('lastName')}
                    className={editForm.formState.errors.lastName ? 'border-destructive' : ''}
                  />
                  {editForm.formState.errors.lastName && (
                    <p className="text-sm text-destructive">
                      {editForm.formState.errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  {...editForm.register('email')}
                  className={editForm.formState.errors.email ? 'border-destructive' : ''}
                />
                {editForm.formState.errors.email && (
                  <p className="text-sm text-destructive">
                    {editForm.formState.errors.email.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-phone">Telefon</Label>
                <Input id="edit-phone" {...editForm.register('phone')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-password">Parolă Nouă (opțional)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  placeholder="Lăsați gol pentru a păstra parola actuală"
                  {...editForm.register('password')}
                />
              </div>
              <div className="space-y-2">
                <Label>Roluri</Label>
                <RoleCheckboxes
                  value={editForm.watch('roles') || []}
                  onChange={(roles) => editForm.setValue('roles', roles, { shouldValidate: true })}
                />
                {editForm.formState.errors.roles && (
                  <p className="text-sm text-destructive">
                    {editForm.formState.errors.roles.message}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setSelectedUser(null);
                  editForm.reset();
                }}
              >
                Anulează
              </Button>
              <Button type="submit" disabled={updateUserMutation.isPending}>
                {updateUserMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  'Salvează Modificările'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmați ștergerea</AlertDialogTitle>
            <AlertDialogDescription>
              Sunteți sigur că doriți să ștergeți utilizatorul{' '}
              <strong>
                {selectedUser?.firstName} {selectedUser?.lastName}
              </strong>
              ? Această acțiune nu poate fi anulată.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedUser(null)}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteUser}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteUserMutation.isPending}
            >
              {deleteUserMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Se șterge...
                </>
              ) : (
                'Șterge'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
